import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { MessageSquare, MoreHorizontal, SearchX } from 'lucide-react';

import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { formatRelativeTime } from '@/lib/relativeTime';
import { igHandle } from '@/lib/igHandle';
import { Badge } from '@/components/ui/badge';
import { AttentionStatusBadge } from '@/components/shared/AttentionStatusBadge';
import { EmptyState } from '@/components/shared/EmptyState';
import { PageHeader } from '@/components/shared/PageHeader';
import {
  conversationService,
  type AttentionStatus,
  type ConversationSummary,
  type ConversationListParams,
} from '@/services/conversationService';

const STATUS_OPTIONS: readonly AttentionStatus[] = [
  'needs_attention',
  'active',
  'resolved',
  'idle',
];
const PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 300;

// Shared height token for all filter-row controls. Mirrors the LeadsPage
// pattern (FILTER_CONTROL_CLASS) to avoid the shadcn Input / raw select
// height mismatch.
export const FILTER_CONTROL_CLASS = 'h-9';

/**
 * Stable cache key for a fetch's params. The bootstrap stamps the key it
 * loaded so the reactive effect can skip re-issuing the identical request —
 * the F102 double-fetch on entry.
 */
function fetchKey(page: number, status: AttentionStatus | '', search: string): string {
  return `${page}::${status}::${search}`;
}

export function ConversationsPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  // `null` = loading; set after first fetch resolves.
  const [items, setItems] = useState<ConversationSummary[] | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  // `undefined` = not yet decided (waiting for initial fetch).
  const [statusFilter, setStatusFilter] = useState<AttentionStatus | '' | undefined>(undefined);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [error, setError] = useState<string | null>(null);

  // F065 sequence guard: each fetch takes a ticket; only the latest ticket may
  // commit state. Without this, a slow in-flight response "wins" over a newer
  // one (stale list after typing a search). See docs/07 §8 "List fetch".
  const fetchSeq = useRef(0);
  // Params the displayed list currently reflects. Bootstrap stamps this so the
  // reactive effect never re-issues the request it already satisfied (F102).
  const lastFetchedKey = useRef<string | null>(null);
  // True once the owner has explicitly chosen a filter — bootstrap must not
  // override that choice if its probe was still in flight when they picked.
  const userPickedRef = useRef(false);

  // Debounce: 300ms standard per §21.5.
  useEffect(() => {
    const handle = window.setTimeout(() => {
      setDebouncedSearch(searchInput);
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [searchInput]);

  // Sequence-guarded list fetch — only the latest ticket commits state.
  const runFetch = useCallback(async (params: ConversationListParams, key: string) => {
    const seq = ++fetchSeq.current;
    setError(null);
    try {
      const response = await conversationService.list(params);
      if (seq !== fetchSeq.current) return; // stale — a newer fetch superseded us
      setItems(response.items);
      setTotal(response.total);
      lastFetchedKey.current = key;
    } catch (e) {
      if (seq !== fetchSeq.current) return; // stale error must not overwrite either
      setError(e instanceof Error ? e.message : t('common.error_generic'));
      setItems([]);
    }
    // `t` is intentionally omitted: react-i18next's `t` is referentially stable
    // across renders, and including it in this fetch-driving callback turns any
    // render that produces a fresh `t` (e.g. the test i18n mock) into an infinite
    // refetch loop — runFetch → setError/setItems → re-render → new t → runFetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // F102 bootstrap — ONE list fetch on the common path. Optimistically query
  // `needs_attention` (the smart default from F042/F065); fall back to an
  // unfiltered fetch ONLY when there is nothing needing attention. Active
  // accounts therefore enter with a single request instead of the old
  // unfiltered-probe-then-filtered pair. The decided params are stamped into
  // `lastFetchedKey` so the reactive effect below does not re-issue them.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const na = await conversationService.list({
          page: 1,
          page_size: PAGE_SIZE,
          status: 'needs_attention',
        });
        if (cancelled || userPickedRef.current) return;
        if (na.items.length > 0) {
          setItems(na.items);
          setTotal(na.total);
          lastFetchedKey.current = fetchKey(1, 'needs_attention', '');
          setStatusFilter('needs_attention');
          return;
        }
        const all = await conversationService.list({ page: 1, page_size: PAGE_SIZE });
        if (cancelled || userPickedRef.current) return;
        setItems(all.items);
        setTotal(all.total);
        lastFetchedKey.current = fetchKey(1, '', '');
        setStatusFilter('');
      } catch (e) {
        if (cancelled || userPickedRef.current) return;
        // Decide on "all" so the controls work, stamp the key so the reactive
        // effect doesn't auto-retry, and surface the error with a retry CTA.
        lastFetchedKey.current = fetchKey(1, '', '');
        setStatusFilter('');
        setError(e instanceof Error ? e.message : t('common.error_generic'));
        setItems([]);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reactive refetch on filter/search/page change. Skips while the default is
  // undecided and when the requested params already match what is displayed
  // (the bootstrap result) — guaranteeing a single fetch on entry.
  useEffect(() => {
    if (statusFilter === undefined) return;
    const key = fetchKey(page, statusFilter, debouncedSearch);
    if (key === lastFetchedKey.current) return;
    const params: ConversationListParams = { page, page_size: PAGE_SIZE };
    if (statusFilter) params.status = statusFilter;
    if (debouncedSearch) params.q = debouncedSearch;
    void runFetch(params, key);
  }, [page, statusFilter, debouncedSearch, runFetch]);

  // Manual retry from the error CTA — refetch the currently-selected params.
  const retry = useCallback(() => {
    const status = statusFilter ?? '';
    const params: ConversationListParams = { page, page_size: PAGE_SIZE };
    if (status) params.status = status;
    if (debouncedSearch) params.q = debouncedSearch;
    void runFetch(params, fetchKey(page, status, debouncedSearch));
  }, [page, statusFilter, debouncedSearch, runFetch]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // F065 #32: a no-result search/filter must explain itself instead of showing
  // the first-use empty state ("conversations will appear here…").
  const hasActiveFilters = Boolean(debouncedSearch) || Boolean(statusFilter);
  const statusLabel = statusFilter ? t(`conversations.status.${statusFilter}`) : '';

  const clearFilters = () => {
    userPickedRef.current = true;
    setSearchInput('');
    setDebouncedSearch('');
    setStatusFilter('');
    setPage(1);
  };

  return (
    <div className="flex flex-col gap-6 p-6">
      <PageHeader title={t('conversations.title')} subtitle={t('conversations.subtitle')} />

      <div className="flex flex-col gap-3 md:flex-row md:items-center md:gap-4">
        <Input
          data-testid="conversations-search-input"
          placeholder={t('conversations.search_placeholder')}
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          className={cn(FILTER_CONTROL_CLASS, 'md:max-w-sm')}
        />
        <select
          data-testid="conversations-status-filter"
          value={statusFilter ?? ''}
          onChange={(event) => {
            userPickedRef.current = true;
            const next = event.target.value as AttentionStatus | '';
            setStatusFilter(next);
            setPage(1);
          }}
          className={cn(FILTER_CONTROL_CLASS, 'rounded-lg border bg-background px-3 text-sm')}
        >
          <option value="">{t('conversations.filter_all')}</option>
          {STATUS_OPTIONS.map((status) => (
            <option key={status} value={status}>
              {t(`conversations.status.${status}`)}
            </option>
          ))}
        </select>
      </div>

      {error ? (
        <div role="alert" className="flex flex-col gap-2">
          <p className="text-sm text-destructive">{error}</p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="min-h-11 w-fit md:min-h-0"
            onClick={retry}
          >
            {t('conversations.retry')}
          </Button>
        </div>
      ) : null}

      {items === null ? (
        <div className="flex flex-col gap-2" data-testid="conversations-skeleton">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : items.length === 0 ? (
        hasActiveFilters ? (
          <EmptyState
            icon={SearchX}
            title={
              debouncedSearch
                ? t('conversations.empty_search.title', { q: debouncedSearch })
                : t('conversations.empty_search.title_filter', { filter: statusLabel })
            }
            subtitle={
              debouncedSearch && statusFilter
                ? t('conversations.empty_search.subtitle_with_filter', { filter: statusLabel })
                : t('conversations.empty_search.subtitle')
            }
            cta={
              <Button
                type="button"
                variant="outline"
                data-testid="conversations-clear-filters"
                onClick={clearFilters}
              >
                {t('conversations.empty_search.cta')}
              </Button>
            }
          />
        ) : (
          <EmptyState
            icon={MessageSquare}
            title={t('conversations.empty.title')}
            subtitle={t('conversations.empty.subtitle')}
          />
        )
      ) : (
        <>
          {/* Desktop table — §21.7: hidden on mobile, cards below. */}
          <div data-testid="conversations-desktop-table" className="hidden md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('conversations.table.username')}</TableHead>
                  <TableHead>{t('conversations.table.snippet')}</TableHead>
                  <TableHead>{t('conversations.table.status')}</TableHead>
                  <TableHead>{t('conversations.table.last_activity')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((conv) => (
                  <TableRow
                    key={conv.contact_id}
                    className="cursor-pointer"
                    onClick={() => navigate(`/conversations/${conv.contact_id}`)}
                    data-testid={`conversation-row-${conv.contact_id}`}
                  >
                    <TableCell className="font-medium">
                      {/* cap + truncate so an unbreakable handle/IGSID can't
                          inflate the column past the wrapper (§21.7) */}
                      <div className="flex items-center gap-2">
                        <Link
                          to={`/conversations/${conv.contact_id}`}
                          className="block max-w-56 truncate hover:underline"
                          data-testid={`conversation-link-${conv.contact_id}`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {igHandle(conv.ig_username) ??
                            t('conversations.contact_fallback', { id: conv.contact_id })}
                        </Link>
                        {conv.known_contact ? (
                          <Badge variant="outline" className="shrink-0">
                            {t('conversations.known')}
                          </Badge>
                        ) : null}
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      <span className="block max-w-xs truncate">
                        {conv.last_message_snippet ?? '—'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <AttentionStatusBadge
                        status={conv.attention_status}
                        data-testid={`conv-status-badge-${conv.contact_id}`}
                      />
                    </TableCell>
                    <TableCell>
                      {formatRelativeTime(conv.last_activity_at, i18n.language)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Mobile cards — §21.7 pattern. */}
          <div data-testid="conversations-mobile-cards" className="flex flex-col gap-3 md:hidden">
            {items.map((conv) => (
              <Link
                key={conv.contact_id}
                to={`/conversations/${conv.contact_id}`}
                className="rounded-lg border p-4 transition-colors hover:bg-muted"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex min-w-0 flex-col">
                    <span className="truncate font-medium">
                      {igHandle(conv.ig_username) ??
                        t('conversations.contact_fallback', { id: conv.contact_id })}
                    </span>
                    {conv.last_message_snippet ? (
                      <span className="line-clamp-1 text-sm text-muted-foreground">
                        {conv.last_message_snippet}
                      </span>
                    ) : null}
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-1">
                    <AttentionStatusBadge status={conv.attention_status} />
                    {conv.known_contact ? (
                      <Badge variant="outline">{t('conversations.known')}</Badge>
                    ) : null}
                  </div>
                </div>
                <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
                  <span>{formatRelativeTime(conv.last_activity_at, i18n.language)}</span>
                  <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
                </div>
              </Link>
            ))}
          </div>

          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <span>{t('conversations.pagination.page', { page, total: totalPages })}</span>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="min-h-11 md:min-h-0"
              >
                {t('conversations.pagination.previous')}
              </Button>
              <Button
                type="button"
                variant="outline"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="min-h-11 md:min-h-0"
              >
                {t('conversations.pagination.next')}
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
